/**
 * Language packs: the one format for every language, built in (packs/en.json, packs/de.json)
 * or added at runtime (files from Ansible, uploads in the admin app).
 *
 *   {
 *     "format": "platform-language-pack",
 *     "formatVersion": 1,
 *     "locale": "fr",
 *     "name": "Français",
 *     "messages": { "common": { "save": "Enregistrer" } }
 *   }
 *
 * `messages` may be nested (like en.json) or flat with dotted keys ("common.save"). English is
 * the reference: it defines the key schema and is the fallback for missing keys. A pack for a
 * language that already exists (built in) overrides only the keys it contains.
 *
 * Validation (same for built-in and runtime packs):
 * - error   → pack rejected: wrong format, bad locale, non-string value, message syntax error,
 *             a placeholder the reference does not have (it could never be filled)
 * - warning → accepted: unknown key (dropped), empty string (dropped), placeholder of the
 *             reference not used by the translation
 */
import { messageArguments, MessageSyntaxError } from './message-format.js';

export const PACK_FORMAT = 'platform-language-pack';
export const PACK_FORMAT_VERSION = 1;

/** Flat message map: dotted key → message. */
export type FlatMessages = Readonly<Record<string, string>>;

export interface NestedMessages {
  readonly [key: string]: string | NestedMessages;
}

/** A language pack file. */
export interface LanguagePack {
  format: typeof PACK_FORMAT;
  formatVersion: typeof PACK_FORMAT_VERSION;
  locale: string;
  name: string;
  messages: NestedMessages;
}

export type PackIssueCode =
  | 'invalid_format'
  | 'invalid_locale'
  | 'locale_mismatch'
  | 'invalid_name'
  | 'not_a_string'
  | 'empty_message'
  | 'unknown_key'
  | 'syntax_error'
  | 'unknown_placeholder'
  | 'missing_placeholder';

export interface PackIssue {
  severity: 'error' | 'warning';
  code: PackIssueCode;
  /** Dotted message key, when the issue is about one message. */
  key?: string;
  message: string;
}

export interface PackCoverage {
  /** Reference keys this pack translates. */
  translated: number;
  /** Keys of the reference (en). */
  total: number;
  /** 0–100, rounded down to one decimal. */
  percent: number;
}

export interface ValidatedPack {
  /** Canonical locale (BCP 47), e.g. `de`, `pt-BR`; undefined when invalid. */
  locale: string | undefined;
  name: string;
  /** Valid messages only (unknown and empty keys dropped). */
  messages: FlatMessages;
  issues: PackIssue[];
  coverage: PackCoverage;
  /** No error-level issues. */
  valid: boolean;
}

const MAX_KEYS = 10_000;
const MAX_MESSAGE_LENGTH = 4_000;

/** Canonical BCP 47 tag limited to language[-Script][-REGION], or undefined. */
export function normalizeLocale(tag: unknown): string | undefined {
  if (typeof tag !== 'string' || !/^[A-Za-z]{2,3}(-[A-Za-z]{4})?(-([A-Za-z]{2}|\d{3}))?$/.test(tag)) return undefined;
  try {
    return Intl.getCanonicalLocales(tag)[0];
  } catch {
    return undefined;
  }
}

/** `de-AT` → `de`; undefined for a tag without region/script. */
export function baseLocale(locale: string): string | undefined {
  const base = locale.split('-')[0];
  return base && base !== locale ? base : undefined;
}

/** Nested or flat message object → flat map. Non-string leaves are reported, not copied. */
export function flattenMessages(input: unknown, issues: PackIssue[] = []): Record<string, string> {
  const flat: Record<string, string> = {};
  let count = 0;
  const walk = (node: unknown, prefix: string) => {
    if (typeof node !== 'object' || node === null || Array.isArray(node)) {
      issues.push({ severity: 'error', code: 'not_a_string', key: prefix, message: `"${prefix}" must be a string` });
      return;
    }
    for (const [part, value] of Object.entries(node as Record<string, unknown>)) {
      const key = prefix ? `${prefix}.${part}` : part;
      if (typeof value === 'string') {
        if (++count > MAX_KEYS) continue;
        flat[key] = value;
      } else {
        walk(value, key);
      }
    }
  };
  walk(input, '');
  if (count > MAX_KEYS) issues.push({ severity: 'error', code: 'invalid_format', message: `More than ${MAX_KEYS} messages` });
  return flat;
}

/** Flat map → nested object (the shape of en.json), keys in the given order. */
export function nestMessages(flat: FlatMessages): NestedMessages {
  const root: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let node = root;
    for (const part of parts.slice(0, -1)) {
      node[part] ??= {};
      node = node[part] as Record<string, unknown>;
    }
    node[parts.at(-1)!] = value;
  }
  return root as NestedMessages;
}

function coverageOf(messages: FlatMessages, reference: FlatMessages): PackCoverage {
  const total = Object.keys(reference).length;
  const translated = Object.keys(messages).filter((key) => key in reference).length;
  return { translated, total, percent: total === 0 ? 100 : Math.floor((translated / total) * 1000) / 10 };
}

function checkMessage(key: string, value: string, referenceValue: string | undefined, issues: PackIssue[]): boolean {
  if (value.trim() === '') {
    issues.push({ severity: 'warning', code: 'empty_message', key, message: `"${key}" is empty and was ignored` });
    return false;
  }
  if (value.length > MAX_MESSAGE_LENGTH) {
    issues.push({ severity: 'error', code: 'invalid_format', key, message: `"${key}" is longer than ${MAX_MESSAGE_LENGTH} characters` });
    return false;
  }
  let args: Set<string>;
  try {
    args = messageArguments(value);
  } catch (error) {
    const detail = error instanceof MessageSyntaxError ? error.message : String(error);
    issues.push({ severity: 'error', code: 'syntax_error', key, message: `"${key}": ${detail}` });
    return false;
  }
  if (referenceValue === undefined) return true;
  const expected = messageArguments(referenceValue);
  const unknown = [...args].filter((name) => !expected.has(name));
  if (unknown.length > 0) {
    issues.push({
      severity: 'error',
      code: 'unknown_placeholder',
      key,
      message: `"${key}" uses {${unknown.join('}, {')}} which English does not provide`,
    });
    return false;
  }
  const missing = [...expected].filter((name) => !args.has(name));
  if (missing.length > 0) {
    issues.push({ severity: 'warning', code: 'missing_placeholder', key, message: `"${key}" does not use {${missing.join('}, {')}}` });
  }
  return true;
}

export interface ValidateOptions {
  /** Reference messages (flat en). Omit only when validating the reference itself. */
  reference?: FlatMessages;
  /** The locale the pack must declare (e.g. from the upload URL or file name). */
  expectedLocale?: string;
}

/** Validates a pack file (parsed JSON). Never throws. */
export function validatePack(input: unknown, { reference, expectedLocale }: ValidateOptions = {}): ValidatedPack {
  const issues: PackIssue[] = [];
  const fail = (message: string): ValidatedPack => ({
    locale: undefined,
    name: '',
    messages: {},
    issues: [...issues, { severity: 'error', code: 'invalid_format', message }],
    coverage: { translated: 0, total: reference ? Object.keys(reference).length : 0, percent: 0 },
    valid: false,
  });
  if (typeof input !== 'object' || input === null || Array.isArray(input)) return fail('A language pack must be a JSON object');
  const pack = input as Partial<Record<keyof LanguagePack, unknown>>;
  if (pack.format !== PACK_FORMAT) return fail(`"format" must be "${PACK_FORMAT}"`);
  if (pack.formatVersion !== PACK_FORMAT_VERSION) return fail(`"formatVersion" must be ${PACK_FORMAT_VERSION}`);

  const locale = normalizeLocale(pack.locale);
  if (!locale) issues.push({ severity: 'error', code: 'invalid_locale', message: `"locale" must be a language tag like "fr" or "pt-BR"` });
  else if (expectedLocale !== undefined && locale !== normalizeLocale(expectedLocale)) {
    issues.push({ severity: 'error', code: 'locale_mismatch', message: `The pack is for "${locale}", expected "${expectedLocale}"` });
  }
  const name = typeof pack.name === 'string' ? pack.name.trim() : '';
  if (!name || name.length > 60) issues.push({ severity: 'error', code: 'invalid_name', message: '"name" must be the language name (1-60 characters), e.g. "Français"' });

  const flat = flattenMessages(pack.messages, issues);
  const messages: Record<string, string> = {};
  for (const [key, value] of Object.entries(flat)) {
    if (reference && !(key in reference)) {
      issues.push({ severity: 'warning', code: 'unknown_key', key, message: `"${key}" is not a known key and was ignored` });
      continue;
    }
    if (checkMessage(key, value, reference?.[key], issues)) messages[key] = value;
  }

  return {
    locale,
    name,
    messages,
    issues,
    coverage: coverageOf(messages, reference ?? messages),
    valid: !issues.some((issue) => issue.severity === 'error'),
  };
}

/**
 * A pack file to translate: every reference key, prefilled with `prefill` (existing
 * translations) or the English text, ready to edit and upload.
 */
export function packTemplate(reference: FlatMessages, options: { locale?: string; name?: string; prefill?: FlatMessages } = {}): LanguagePack {
  const merged: Record<string, string> = {};
  for (const [key, value] of Object.entries(reference)) merged[key] = options.prefill?.[key] ?? value;
  return {
    format: PACK_FORMAT,
    formatVersion: PACK_FORMAT_VERSION,
    locale: options.locale ?? 'xx',
    name: options.name ?? 'Language name',
    messages: nestMessages(merged),
  };
}
